/**
 * CustomerInlineActivityCreator - Criador inline de andamentos na timeline do cliente
 *
 * Réplica funcional/visual de `DealInlineActivityCreator` (modal de negócios),
 * adaptada para o contexto de CLIENTE. Componente colapsado/expansível exibido
 * no topo da timeline, com os botões dinâmicos de tipo de atividade
 * (ligação, reunião, e-mail, lembrete, nota).
 *
 * Reaproveita 100% do que já existe:
 *  - CRM_ACTIVITY_CONFIG (configuração compartilhada de tipos)
 *  - useCustomerActivityCreate (RPC create_customer_activity)
 *  - useActivityAttachments (upload de anexos)
 *  - RichTextEditor / SmartAvatar
 *
 * @version 1.2.0
 * RELEASE: 2026-08-09 v1.2.0 - Refetch da timeline SOMENTE após concluir o upload
 *          dos anexos (antes a lista era invalidada antes dos uploads e os anexos
 *          só apareciam após F5)
 * RELEASE: 2026-08-09 v1.0.0 - Criação inicial replicando DealInlineActivityCreator
 */
import React, { useState, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  X,
  Loader2,
  Upload,
  File as FileIcon,
  Paperclip,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import {
  MentionPickerField,
  type MentionableMember,
} from '@/components/shared/MentionPickerField';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useAuth } from '@/hooks/useAuth';
import { useCustomerActivityCreate } from '@/hooks/useCustomerActivityCreate';
import { useActivityAttachments } from '@/hooks/useActivityAttachments';
import { logger } from '@/lib/logger';
import {
  CRM_ACTIVITY_CONFIG,
  type CrmFormActivityType,
} from '@/components/crm/deals/modal/components/activityConfig';

/** Props do componente */
interface CustomerInlineActivityCreatorProps {
  /** ID do cliente ao qual o andamento será vinculado (null no modo Contato) */
  customerId: string | null;
  /** v1.1.0: ID do contato (modo Contato, sem cliente vinculado) */
  contactId?: string | null;
  /** Permite criação (false quando sem permissão) */
  canCreate?: boolean;
  /** Callback opcional após criar com sucesso */
  onCreated?: () => void;
}

/** Arquivo staged (ainda não enviado) */
interface StagedFile {
  id: string;
  file: File;
  preview?: string;
}

/** Ordem dos tipos exibidos no seletor (mesma da QuickActivityBar) */
const TYPE_ORDER: CrmFormActivityType[] = ['call', 'meeting', 'email', 'task', 'note'];

/**
 * Criador inline de andamentos no topo da timeline do cliente
 */
export function CustomerInlineActivityCreator({
  customerId,
  contactId = null,
  canCreate = true,
  onCreated,
}: CustomerInlineActivityCreatorProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { createActivity } = useCustomerActivityCreate(customerId, contactId);
  const { uploadFile } = useActivityAttachments(null);

  // Estado de UI
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [activityType, setActivityType] = useState<CrmFormActivityType>('note');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [richDescription, setRichDescription] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);
  // RELEASE: 2026-09-12 - pessoas marcadas já na criação do andamento
  const [mentionedUsers, setMentionedUsers] = useState<MentionableMember[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const config = CRM_ACTIVITY_CONFIG[activityType];

  /** Reseta o formulário ao estado inicial */
  const resetForm = useCallback(() => {
    setTitle('');
    setDescription('');
    setRichDescription('');
    setScheduledAt('');
    setMentionedUsers([]);
    stagedFiles.forEach((sf) => sf.preview && URL.revokeObjectURL(sf.preview));
    setStagedFiles([]);
  }, [stagedFiles]);

  /** Cancela e colapsa o formulário */
  const handleCancel = useCallback(() => {
    resetForm();
    setActivityType('note');
    setIsFormOpen(false);
  }, [resetForm]);

  /** Adiciona arquivos staged (validação de 10MB e limite de 10) */
  const handleFilesSelected = useCallback((files: FileList | null) => {
    if (!files) return;
    const newFiles: StagedFile[] = [];
    Array.from(files).forEach((file) => {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name}: Arquivo muito grande (máx 10MB)`);
        return;
      }
      const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const preview = file.type.startsWith('image/')
        ? URL.createObjectURL(file)
        : undefined;
      newFiles.push({ id, file, preview });
    });
    setStagedFiles((prev) => [...prev, ...newFiles].slice(0, 10));
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  /** Remove arquivo staged */
  const removeStagedFile = useCallback((id: string) => {
    setStagedFiles((prev) => {
      const target = prev.find((f) => f.id === id);
      if (target?.preview) URL.revokeObjectURL(target.preview);
      return prev.filter((f) => f.id !== id);
    });
  }, []);

  /** Drag & drop de arquivos */
  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      handleFilesSelected(e.dataTransfer.files);
    },
    [handleFilesSelected]
  );

  /** Submete a criação do andamento + upload de anexos */
  const handleSubmit = useCallback(async () => {
    const finalTitle = title.trim();
    if (!finalTitle) {
      toast.error('Informe um título para a atividade');
      return;
    }
    if (finalTitle.length > 100) {
      toast.error('Título muito longo (máx. 100 caracteres)');
      return;
    }

    const finalDescription = config.showRichText ? richDescription : description;
    if (finalDescription && finalDescription.length > 5000) {
      toast.error('Descrição muito longa (máx. 5000 caracteres)');
      return;
    }

    setIsSubmitting(true);
    try {
      const activityId = await createActivity.mutateAsync({
        customer_id: customerId,
        contact_id: contactId,
        activity_type: activityType,
        title: finalTitle,
        description: finalDescription || null,
        scheduled_at: scheduledAt || null,
        // RELEASE: 2026-09-12 - menções gravadas junto com o andamento
        mentioned_user_ids: mentionedUsers.map((u) => u.id),
      });

      // Upload de anexos (apenas quando permitido pelo tipo)
      if (config.showAttachments && stagedFiles.length > 0 && activityId) {
        await Promise.all(
          stagedFiles.map((sf) =>
            uploadFile({ activityId, file: sf.file }).catch((err: any) => {
              logger.error(`Erro ao enviar ${sf.file.name}:`, err?.message || err);
              return null;
            })
          )
        );

        // v1.2.0: só agora (com os anexos já persistidos) a timeline é recarregada,
        // garantindo que os arquivos apareçam sem precisar de F5
        await queryClient.invalidateQueries({ queryKey: ['customer-activities-paginated'] });
        await queryClient.invalidateQueries({ queryKey: ['customer-activities'] });
      }


      toast.success(`${config.title.replace(/^Nov[ao] /, '')} registrada com sucesso`);
      resetForm();
      setActivityType('note');
      setIsFormOpen(false);
      onCreated?.();
    } catch (error: any) {
      logger.error('Erro ao criar andamento inline:', error?.message || error);
      toast.error('Erro ao salvar atividade');
    } finally {
      setIsSubmitting(false);
    }
  }, [
    title,
    description,
    richDescription,
    config,
    scheduledAt,
    stagedFiles,
    mentionedUsers,
    createActivity,
    customerId,
    activityType,
    uploadFile,
    resetForm,
    onCreated,
    queryClient,
  ]);

  // ==============================================================
  // Render — estado colapsado
  // ==============================================================
  if (!isFormOpen) {
    return (
      <button
        type="button"
        onClick={() => canCreate && setIsFormOpen(true)}
        disabled={!canCreate}
        className={cn(
          'w-full flex items-center gap-3 p-3 rounded-lg border border-dashed',
          'text-muted-foreground hover:text-foreground hover:border-primary/40',
          'hover:bg-muted/30 transition-all cursor-text text-left',
          !canCreate && 'opacity-60 cursor-not-allowed hover:bg-transparent'
        )}
      >
        <SmartAvatar
          name={user?.user_metadata?.full_name || user?.email}
          src={user?.user_metadata?.avatar_url}
          size="sm"
          className="flex-shrink-0"
        />
        <span className="text-sm">
          {canCreate
            ? 'Adicionar atividade...'
            : 'Não é possível adicionar atividades neste cliente'}
        </span>
      </button>
    );
  }

  // ==============================================================
  // Render — estado expandido
  // ==============================================================
  const Icon = config.icon;
  return (
    <TooltipProvider>
      <div className="rounded-lg border bg-card shadow-sm">
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/30 rounded-t-lg">
          <div className="flex items-center gap-2">
            <div className={cn('p-1.5 rounded-md', config.bgClass)}>
              <Icon className={cn('h-4 w-4', config.color)} />
            </div>
            <span className="text-sm font-medium">{config.title}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              <X className="h-3.5 w-3.5 mr-1" />
              Cancelar
            </Button>
            <Button
              size="sm"
              className="h-7 text-xs"
              onClick={handleSubmit}
              disabled={isSubmitting || !title.trim()}
            >
              {isSubmitting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
              ) : (
                <Plus className="h-3.5 w-3.5 mr-1" />
              )}
              Criar
            </Button>
          </div>
        </div>

        {/* Seletor de tipo — botões dinâmicos (mesmo visual da QuickActivityBar) */}
        <div className="flex items-center gap-1 px-3 py-2 border-b bg-background">
          {TYPE_ORDER.map((type) => {
            const cfg = CRM_ACTIVITY_CONFIG[type];
            const TypeIcon = cfg.icon;
            const isActive = type === activityType;
            return (
              <Tooltip key={type}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => setActivityType(type)}
                    className={cn(
                      'h-8 w-8 rounded-lg flex items-center justify-center transition-all',
                      isActive
                        ? cn(cfg.bgClass, cfg.color, 'ring-2 ring-primary/40')
                        : 'text-muted-foreground hover:bg-muted'
                    )}
                    aria-pressed={isActive}
                  >
                    <TypeIcon className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p className="text-xs">{cfg.title.replace(/^Nov[ao] /, '')}</p>
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>

        {/* Corpo do formulário */}
        <div className="p-3 space-y-3">
          {/* Título */}
          <div className="space-y-1.5">
            <Label htmlFor="customer-inline-activity-title" className="text-xs font-medium">
              Título <span className="text-destructive">*</span>
            </Label>
            <Input
              id="customer-inline-activity-title"
              placeholder={config.titlePlaceholder}
              className="h-9 text-sm"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={100}
              autoFocus
            />
          </div>

          {/* Descrição */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Descrição</Label>
            {config.showRichText ? (
              <RichTextEditor
                content={richDescription}
                onChange={setRichDescription}
                placeholder="Adicione detalhes sobre a atividade..."
                minHeight="120px"
                maxHeight="280px"
                fontSize="sm"
                showToolbar
              />
            ) : (
              <Textarea
                placeholder="Adicione detalhes sobre a atividade..."
                className="min-h-[80px] text-sm resize-none"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={5000}
              />
            )}
          </div>

          {/* Menções (RELEASE 2026-09-12) */}
          <MentionPickerField
            value={mentionedUsers}
            onChange={setMentionedUsers}
            disabled={isSubmitting}
          />

          {/* Data/Hora */}
          {config.showSchedule && (
            <div className="space-y-1.5">
              <Label htmlFor="customer-inline-activity-date" className="text-xs font-medium">
                Data e Hora
              </Label>
              <Input
                id="customer-inline-activity-date"
                type="datetime-local"
                className="h-9 text-sm"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
              />
            </div>
          )}

          {/* Anexos (apenas para nota) */}
          {config.showAttachments && (
            <div className="space-y-2">
              <Label className="text-xs font-medium flex items-center gap-2">
                <Paperclip className="h-3.5 w-3.5" />
                Anexos
                {stagedFiles.length > 0 && (
                  <span className="text-muted-foreground">
                    ({stagedFiles.length}/10)
                  </span>
                )}
              </Label>

              {/* Dropzone */}
              <div
                className={cn(
                  'border-2 border-dashed rounded-lg p-3 text-center cursor-pointer transition-colors',
                  'hover:border-primary/50 hover:bg-muted/30'
                )}
                onClick={() => fileInputRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
              >
                <Upload className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
                <p className="text-xs text-muted-foreground">
                  Arraste arquivos ou clique para adicionar
                </p>
                <p className="text-[10px] text-muted-foreground/70 mt-0.5">
                  Máximo 10MB por arquivo
                </p>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                className="hidden"
                onChange={(e) => handleFilesSelected(e.target.files)}
              />

              {/* Miniaturas dos arquivos staged */}
              {stagedFiles.length > 0 && (
                <div className="grid grid-cols-6 gap-2">
                  {stagedFiles.map((sf) => (
                    <div
                      key={sf.id}
                      className="relative group aspect-square rounded-md overflow-hidden ring-1 ring-border"
                    >
                      {sf.preview ? (
                        <img
                          src={sf.preview}
                          alt={sf.file.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-muted flex flex-col items-center justify-center p-1">
                          <FileIcon className="h-5 w-5 text-muted-foreground mb-0.5" />
                          <span className="text-[8px] text-muted-foreground text-center line-clamp-2">
                            {sf.file.name}
                          </span>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => removeStagedFile(sf.id)}
                        className={cn(
                          'absolute top-0.5 right-0.5 h-4 w-4 rounded-full',
                          'bg-destructive text-destructive-foreground',
                          'opacity-0 group-hover:opacity-100 transition-opacity',
                          'flex items-center justify-center'
                        )}
                      >
                        <X className="h-2.5 w-2.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}
